import DataTable from "@/components/DataTable";
import TradeToolShell, { type ToolCategory } from "@/components/TradeToolShell";
import {
  floorJoistHeaders, floorJoistRows,
  steelBeamHeaders, steelBeamRows,
  headerSpanHeaders, headerSpanRows,
  anchorHeaders, anchorRows,
  seismicBraceHeaders, seismicBraceRows,
  designLoadHeaders, designLoadRows,
} from "@/components/structural/structural-data";
import { SeismicForceCalc, AnchorBoltCalc, BeamLoadCalc, DeflectionCalc, SdcLookup } from "@/components/structural/StructuralCalcs";
import TradeFormulas from "@/components/TradeFormulas";

const STRUCTURAL_CATEGORIES: ToolCategory[] = [
  {
    id: "beams", label: "Beams & Loads", color: "bg-primary",
    tools: [
      { value: "joists", label: "Joists" },
      { value: "steelbeam", label: "Steel" },
      { value: "headers", label: "Headers" },
      { value: "beamcalc", label: "Load Calc" },
      { value: "deflection", label: "Deflect." },
      { value: "loads", label: "Loads" },
    ],
  },
  {
    id: "anchors", label: "Anchors", color: "bg-copper",
    tools: [
      { value: "anchors", label: "All Anchors" },
      { value: "anchorcalc", label: "Sizing" },
    ],
  },
  {
    id: "seismic", label: "Seismic", color: "bg-safety",
    tools: [
      { value: "seismicforce", label: "Fp Calc" },
      { value: "bracing", label: "Bracing" },
      { value: "sdc", label: "SDC" },
      { value: "formulas", label: "Formulas" },
    ],
  },
];

const structFav = (category: string) => ({
  type: "structural" as const,
  category,
  makeId: (row: (string | number)[]) => `struct-${category}-${String(row[0]).replace(/\W/g, "")}`,
  makeName: (row: (string | number)[]) => String(row[0]),
  makeDetail: (row: (string | number)[]) => row.slice(1, 3).join(" · "),
});

const renderTool = (activeTool: string) => {
  switch (activeTool) {
    case "joists": return <div className="-mx-4"><DataTable headers={floorJoistHeaders} rows={floorJoistRows} searchable searchPlaceholder="Search joist spans…" favoritesConfig={structFav("🏗️ Joist Spans")} /></div>;
    case "steelbeam": return <div className="-mx-4"><DataTable headers={steelBeamHeaders} rows={steelBeamRows} searchable searchPlaceholder="Search steel beams…" favoritesConfig={structFav("🏗️ Steel Beams")} /></div>;
    case "headers": return <div className="-mx-4"><DataTable headers={headerSpanHeaders} rows={headerSpanRows} searchable searchPlaceholder="Search header spans…" favoritesConfig={structFav("🏗️ Header Spans")} /></div>;
    case "beamcalc": return <BeamLoadCalc />;
    case "deflection": return <DeflectionCalc />;
    case "loads": return <div className="-mx-4"><DataTable headers={designLoadHeaders} rows={designLoadRows} searchable searchPlaceholder="Search design loads…" favoritesConfig={structFav("🏗️ Design Loads")} /></div>;
    case "anchors": return <div className="-mx-4"><DataTable headers={anchorHeaders} rows={anchorRows} searchable searchPlaceholder="Search anchors…" favoritesConfig={structFav("🏗️ Anchors")} /></div>;
    case "anchorcalc": return <AnchorBoltCalc />;
    case "seismicforce": return <SeismicForceCalc />;
    case "bracing": return <div className="-mx-4"><DataTable headers={seismicBraceHeaders} rows={seismicBraceRows} searchable searchPlaceholder="Search bracing intervals…" favoritesConfig={structFav("🏗️ Seismic Bracing")} /></div>;
    case "sdc": return <SdcLookup />;
    case "formulas": return <TradeFormulas trade="structural" />;
    default: return null;
  }
};

const Structural = () => (
  <TradeToolShell
    title="🏗️ Structural"
    subtitle="Beam spans, anchors, seismic bracing & load tables"
    categories={STRUCTURAL_CATEGORIES}
    renderTool={renderTool}
  />
);

export default Structural;
